import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import MuiBottomNavigation from "@mui/material/BottomNavigation";
import BottomNavigationAction from "@mui/material/BottomNavigationAction";
import Paper from "@mui/material/Paper";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

// Icons
import HomeIcon from "@mui/icons-material/Home";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import SchoolIcon from "@mui/icons-material/School";
import PersonIcon from "@mui/icons-material/Person";
import WorkIcon from "@mui/icons-material/Work";
import SearchIcon from "@mui/icons-material/Search";
import BusinessIcon from "@mui/icons-material/Business";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import ListAltIcon from "@mui/icons-material/ListAlt";

/**
 * Perfis:
 * - aluno (CPF, sem labs_profile)
 * - instrutor (CPF, com labs_profile não-vazio)
 * - empresa (CNPJ)
 * - admin (responsabilites === "10" ou "100")
 */

const NAV_ITEMS = {
  aluno: [
    { label: "Início", icon: <HomeIcon />, link: "/dashboard" },
    { label: "Trilhas", icon: <MenuBookIcon />, link: "/oxetrilhas" },
    { label: "Academy", icon: <EmojiEventsIcon />, link: "/oxetechacademy" },
    { label: "Perfil", icon: <PersonIcon />, link: "/account" },
  ],
  instrutor: [
    { label: "Início", icon: <HomeIcon />, link: "/dashboard" },
    { label: "Turmas", icon: <ListAltIcon />, link: "/minhas-turmas-instrutor" },
    { label: "Academy", icon: <EmojiEventsIcon />, link: "/oxetechacademy" },
    { label: "Perfil", icon: <PersonIcon />, link: "/account" },
  ],
  empresa: [
    { label: "Vagas", icon: <WorkIcon />, link: "/dashboard" },
    { label: "Talentos", icon: <SearchIcon />, link: "/bancotalentos" },
    { label: "Work", icon: <BusinessIcon />, link: "/oxe-tech-work" },
    { label: "Perfil", icon: <PersonIcon />, link: "/empresa-account" },
  ],
  admin: [
    { label: "Dashboard", icon: <DashboardIcon />, link: "/dashadmin" },
    { label: "Alunos", icon: <PeopleIcon />, link: "/alunos" },
    { label: "Academy", icon: <EmojiEventsIcon />, link: "/admin-academy" },
    { label: "Suporte", icon: <SupportAgentIcon />, link: "/admin-suporte" },
  ],
};

// Rotas onde o BottomNav NÃO deve aparecer (ex: salas de aula ao vivo)
const HIDDEN_ROUTES = ["/live-class", "/login", "/cadastro", "/empresa-cadastro", "/select-cadastro", "/forgot", "/oxetech-work/inscricao"];

function getUserRole(profile, labsProfile) {
  if (!profile) return null;

  const resp = profile?.responsabilites || profile?.dataValues?.responsabilites;
  if (resp === "10" || resp === "100") return "admin";

  const cnpj = profile?.cnpj || profile?.dataValues?.cnpj;
  if (cnpj) return "empresa";

  if (Array.isArray(labsProfile) && labsProfile.length > 0) return "instrutor";

  return "aluno";
}

function findActiveIndex(items, pathname) {
  // Exact match first
  const exact = items.findIndex((item) => item.link === pathname);
  if (exact !== -1) return exact;

  // Prefix match (for nested routes like /painel-instrutor/123)
  const prefix = items.findIndex(
    (item) => item.link !== "/" && pathname.startsWith(item.link)
  );
  if (prefix !== -1) return prefix;

  return -1;
}

export default function AppBottomNavigation() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();

  const signed = useSelector((state) => state.auth?.signed);
  const profile = useSelector((state) => state.user?.profile);
  const labsProfile = useSelector((state) => state.user?.labs_profile);

  const role = useMemo(() => getUserRole(profile, labsProfile), [profile, labsProfile]);

  const items = role ? NAV_ITEMS[role] || NAV_ITEMS.aluno : [];

  const activeIndex = useMemo(
    () => findActiveIndex(items, location.pathname),
    [items, location.pathname]
  );

  // Não renderizar se: não é mobile, não está logado, não tem role, ou rota está na lista de exclusão
  const isHidden = HIDDEN_ROUTES.some((route) => location.pathname.startsWith(route));

  if (!isMobile || !signed || !role || isHidden) {
    return null;
  }

  return (
    <>
      {/* Spacer para evitar que o conteúdo fique atrás do bottom nav */}
      <div style={{ height: 56 }} />

      <Paper
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          borderTop: "1px solid rgba(0,0,0,0.08)",
        }}
        elevation={8}
      >
        <MuiBottomNavigation
          value={activeIndex}
          showLabels
          sx={{
            height: 56,
            "& .MuiBottomNavigationAction-root": {
              minWidth: "auto",
              padding: "6px 0",
              color: "rgba(0,0,0,0.5)",
              "&.Mui-selected": {
                color: "#D46D0D",
              },
            },
            "& .MuiBottomNavigationAction-label": {
              fontSize: "0.68rem",
              "&.Mui-selected": {
                fontSize: "0.72rem",
                fontWeight: 600,
              },
            },
          }}
        >
          {items.map((item, index) => (
            <BottomNavigationAction
              key={item.link}
              label={item.label}
              icon={item.icon}
              component={Link}
              to={item.link}
            />
          ))}
        </MuiBottomNavigation>
      </Paper>
    </>
  );
}